import { Typography } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import { TableRowSelector } from '../shared/selection-checkbox';
import { RequestTypeCell } from './cells/type-cell';
import { StatusChip } from './cells/status-chip';
import { ExpiryCell } from './cells/expiry-cell';
import { CostCell } from './cells/cost-cell';
import { ApprovalActionsCell } from './cells/actions-cell';
import { isDecidable } from './utils';
import {
  CardApprovalRequest,
  CardRequestStatus,
  CardRequestType
} from '../../../../queries/pxtraCard';

const columnHelper = createColumnHelper<CardApprovalRequest>();

// Only what the list can hold: it carries the requests still awaiting a
// decision, and nothing in the product requests a physical card yet.
const requestTypes: CardRequestType[] = ['virtual_card'];
const requestStatuses: CardRequestStatus[] = ['open', 'failed'];

type Props = {
  canApprove: boolean;
  onApprove: (request: CardApprovalRequest) => void;
  onReject: (request: CardApprovalRequest) => void;
};

export const useCardApprovalColumns = ({ canApprove, onApprove, onReject }: Props) => {
  const { t } = useTranslation();

  return [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => <TableRowSelector table={table} />,
      cell: ({ row }) => (
        <TableRowSelector row={row} disabled={!canApprove || !isDecidable(row.original)} />
      )
    }),
    columnHelper.accessor('status', {
      header: t('common:STATUS'),
      cell: ({ row }) => <StatusChip request={row.original} />,
      meta: {
        filterVariant: 'select',
        filterOptions: requestStatuses.map((status) => ({
          value: status,
          text: t(`pxtraCard:status_${status}`)
        }))
      }
    }),
    columnHelper.accessor('card_kind', {
      header: t('pxtraCard:requestType'),
      cell: ({ row }) => <RequestTypeCell type={row.original.card_kind} />,
      meta: {
        filterVariant: 'select',
        filterOptions: requestTypes.map((type) => ({
          value: type,
          text: t(`pxtraCard:type_${type}`)
        }))
      }
    }),
    columnHelper.accessor((row) => `${row.first_name} ${row.last_name}`, {
      id: 'employee',
      header: t('common:EMPLOYEES'),
      cell: ({ getValue }) => (
        <Typography variant="body2" className="whitespace-nowrap">
          {getValue()}
        </Typography>
      ),
      enableColumnFilter: false
    }),
    columnHelper.accessor('expires_on', {
      header: t('pxtraCard:expiresOn'),
      cell: ({ row }) => <ExpiryCell request={row.original} />,
      enableColumnFilter: false
    }),
    columnHelper.accessor((row) => row.cost.one_off_cents + row.cost.recurring_monthly_cents, {
      id: 'cost',
      header: t('pxtraCard:cost'),
      cell: ({ row }) => <CostCell cost={row.original.cost} />,
      enableColumnFilter: false
    }),
    columnHelper.display({
      id: 'actions',
      header: t('common:ACTIONS'),
      cell: ({ row }) => (
        <ApprovalActionsCell
          request={row.original}
          canApprove={canApprove}
          onApprove={onApprove}
          onReject={onReject}
        />
      )
    })
  ];
};
